/**
 * GENERATED_ASSET — crooked blue ribbon ported from the batch001 static kit (`static-kit/source/ribbon.ts`).
 * Art is unchanged; only layout was expanded for readability.
 *
 * The `tie` anchor (0,0) is the knot: the crooked loop rises above it and two unequal tails (long + short,
 * `shortTailRatio`) hang below it. Origin is an attachment at the tie.
 */
import type { DraftArtifact, Point2, RibbonRecipe } from './contracts';
import { Construction, anchor, el, n } from './construction';

export function buildCrookedRibbon(c: Construction): DraftArtifact {
  const r = c.recipe as RibbonRecipe;
  const p = r.params;
  const L = p.tailLength;
  const S = L * p.shortTailRatio;
  const w = p.width;
  const bend = p.pose === 'left_breeze' ? -12 : p.pose === 'right_breeze' ? 14 : 3;
  const long = { x: 10 + bend, y: L };
  const short = { x: 26 + bend * 0.7, y: S };
  const longControls: Point2[] = [
    { x: -4, y: 1 }, { x: 3 + bend, y: 16 }, { x: -7 + bend, y: L * 0.62 }, { x: long.x - w / 2, y: L },
    { x: long.x + w / 2, y: L }, { x: 7 + bend, y: L * 0.59 }, { x: 13 + bend, y: 13 }, { x: 4, y: -2 },
  ];
  const shortControls: Point2[] = [
    { x: 2, y: -2 }, { x: 18, y: 4 }, { x: 24 + bend, y: S * 0.65 }, { x: short.x + w / 2, y: S },
    { x: short.x - w / 2, y: S }, { x: 18 + bend, y: S * 0.6 }, { x: 11, y: 10 }, { x: -3, y: 2 },
  ];
  const notch = (tip: Point2, half: number): string => (p.fray === 0
    ? `L${n(tip.x + half)} ${n(tip.y)}`
    : p.fray === 1
      ? `L${n(tip.x)} ${n(tip.y - 3)}L${n(tip.x + half)} ${n(tip.y)}`
      : `L${n(tip.x - half * 0.3)} ${n(tip.y - 3)}L${n(tip.x + half * 0.25)} ${n(tip.y - 1)}`
        + `L${n(tip.x + half * 0.6)} ${n(tip.y - 4)}L${n(tip.x + half)} ${n(tip.y)}`);
  const ld = `M-4 1C${n(3 + bend)} 16 ${n(-7 + bend)} ${n(L * 0.62)} ${n(long.x - w / 2)} ${n(L)}${notch(long, w / 2)}`
    + `C${n(7 + bend)} ${n(L * 0.59)} ${n(13 + bend)} 13 4 -2Z`;
  // Reverse short tip order so the rear tail retains a separate visible outline.
  const sd = `M2 -2C18 4 ${n(24 + bend)} ${n(S * 0.65)} ${n(short.x + w / 2)} ${n(S)}`
    + `L${n(short.x)} ${n(S - (p.fray ? 2 : 0))}L${n(short.x - w / 2)} ${n(S)}C${n(18 + bend)} ${n(S * 0.6)} 11 10 -3 2Z`;
  let body = c.path(sd, shortControls, c.color('clothBlueShadow'), c.color('outline'), 1.1, { 'data-part': 'short-tail' });
  body += c.path(ld, longControls, c.color('clothBlue'), c.color('outline'), 1.2, { 'data-part': 'long-tail' });
  const clip = c.define('long-tail', (id) => el('clipPath', { id }, el('path', { d: ld })));
  body += el('g', { 'clip-path': clip }, c.path(`M2 6Q${n(7 + bend)} ${n(L * 0.4)} ${n(long.x + 1)} ${n(L - 8)}`,
    [{ x: 2, y: 6 }, { x: 7 + bend, y: L * 0.4 }, { x: long.x + 1, y: L - 8 }], 'none', c.color('clothBlueLight'), 1.6, {}, false));
  if (p.seam === 'short-repair') {
    const dy = r.detail === 'readable' ? 0 : (c.random('cloth-stitch.offset') - 0.5) * 2;
    body += el('g', { 'clip-path': clip, 'data-part': 'cloth-repair' }, c.path(`M0 ${n(12 + dy)}l4 -1m-4 6l4 -1m-4 6l4 -1`,
      [{ x: 0, y: 11 + dy }, { x: 4, y: 23 + dy }], 'none', c.color('paperLight'), 0.8, {}, false));
  }
  body += c.path('M-2 -2C-22 -17 -26 -1 -7 5L-1 1C13 -16 25 -6 9 2Z',
    [{ x: -2, y: -2 }, { x: -22, y: -17 }, { x: -26, y: -1 }, { x: -7, y: 5 }, { x: -1, y: 1 }, { x: 13, y: -16 }, { x: 25, y: -6 }, { x: 9, y: 2 }],
    c.color('clothBlue'), c.color('outline'), 1.2, { 'data-part': 'crooked-loop' });
  body += c.path('M-6 -5L3 -7L8 1L-2 7L-7 1Z', [{ x: -6, y: -5 }, { x: 3, y: -7 }, { x: 8, y: 1 }, { x: -2, y: 7 }, { x: -7, y: 1 }],
    c.color('clothBlueLight'), c.color('outline'), 1.2, { 'data-part': 'knot' });
  const a = c.finish(body, [
    anchor('tie', 0, 0), anchor('knot_center', 0, 0), anchor('tail_long_tip', long.x, long.y), anchor('tail_short_tip', short.x, short.y),
  ], [], { kind: 'attachment', point: { x: 0, y: 0 } },
  { longTailLength: L, shortTailLength: S, longControls, shortControls, pose: p.pose });
  return { ...a, protectedRegions: [{ name: 'ribbon_silhouette', role: 'motif', bounds: a.contentBounds, gameplayAuthority: false }] };
}
